import React from 'react';
import {
  MobileHeader, SectionNav, Card, EmptyState, ErrorState, Button, Badge,
  Alert, ConfirmDialog,
  useToast,
} from '@parsina/framework';
import { CalendarX, CheckCircle2, FileText, Video } from 'lucide-react';
import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useDentalChart } from '../../hooks/useDentalChart.js';
import {
  useVisit, useVisitNote, usePriorVisits, useTreatmentPlans, useVisitMedications,
  useVisitOrders, useVisitDiagnoses, useClinicalAssistant, useVisitFiles, useSoapAssistant,
} from '../../hooks/useVisit.js';
import {
  VISIT_TABS, planTeethIndex, isVisitEditable,
  DEFAULT_FOLLOW_UP_INTERVAL, followUpInputFor, outstandingForFollowUp, followUpDuration,
} from './Visit.shared.js';
import MobileAllergyStrip from '../../components/mobile/MobileAllergyStrip.jsx';
import { useVideoVisit } from '../../hooks/useVideoVisit.js';
import VisitCall from './sections/VisitCall.jsx';
import VisitOdontogram from './sections/VisitOdontogram.jsx';
import VisitSubjective from './sections/VisitSubjective.jsx';
import VisitTreatmentPlan from './sections/VisitTreatmentPlan.jsx';
import FinishVisitDialog, { finishToast } from './sections/FinishVisitDialog.jsx';
import MedicationsPane from './panes/MedicationsPane.jsx';
import OrdersPane from './panes/OrdersPane.jsx';
import DiagnosisPane from './panes/DiagnosisPane.jsx';
import QuestionsPane from './panes/QuestionsPane.jsx';
import FilesPane from './panes/FilesPane.jsx';
import { VisitSkeletonMobile } from './Visit.skeleton.jsx';

// جلسهٔ درمان on a phone (DESIGN-CONCEPT-MOBILE §4C, applied to a workspace).
//
// ┌──────────────────────────────────────┐
// │ ① MobileHeader — CONTENT mode        │  ← back · patient · date
// │ ② MobileAllergyStrip — STICKY        │  never scrolls off
// │ ③ the persistent clinical view       │  odontogram, then subjective and
// │    (odontogram always open; the two  │  طرح درمان as collapsibles
// │     others collapsible)              │
// │ ④ SectionNav — STICKY                │  دارو · دستورات · تشخیص · …
// │ ⑤ the active pane                    │
// └──────────────────────────────────────┘
//
// ── Why this is not the desktop layout reflowed ────────────────────────────
// §2's two columns cannot both be columns at 390px — halving the viewport
// would leave the odontogram at roughly 180px wide, and a crown is ~10px on a
// phone already. So the two columns become two REGIONS of one scroll, in the
// order the document gives them: the persistent clinical view first, the
// dynamic workspace under it.
//
// What §2 actually asks for is that the dental context is never more than a
// glance away, and on a phone that is answered by keeping the ARCH at the top
// with the allergy strip pinned above it and the pane navigator pinned below —
// not by splitting a phone in half.
//
// ── The odontogram is rendered VERBATIM ────────────────────────────────────
// Not forked, not zoomed, not wrapped in a transform. CORE.md §9 makes the
// patient's anatomical frame a safety invariant, and it holds on this surface
// exactly as it does on چارت دندان's.
//
// ── No bottom nav ──────────────────────────────────────────────────────────
// §4C: a drilled-into full-screen surface carries its own way back in the
// header. A bar offering «بیماران» here is an escape hatch out of a visit the
// dentist is halfway through documenting.

const VisitMobile = ({ appointmentId, tab, setTab, goBack }) => {
  const { t, formatNumber, formatDate } = useTranslation();
  const navigate = useAppNavigate();
  const toast = useToast();
  const { visit, loading, error, reload, completing, complete, followUpOutcome } = useVisit(appointmentId);

  const patientId = visit?.patientId || null;
  const clinicId = visit?.clinicId || null;
  const editable = isVisitEditable(visit);

  // ویزیت تصویری — the SAME hook the desktop surface uses. Two surfaces, one
  // behaviour: the panel and every rule about when a call may start live in
  // shared code, and only the chrome around them differs.
  const call = useVideoVisit({ patientId, appointmentId, editable });
  React.useEffect(() => { call.probe(); }, [call.probe]);

  const [selectedTooth, setSelectedTooth] = React.useState(null);
  // Each CLICK on the arch, as an event: `{ fdi, seq }` with a counter that
  // rises on every click, including a repeat click on the tooth already
  // selected. «دندان‌های درگیر» toggles on it, which is how a complaint comes
  // to name several teeth without the chart having to hold a selection SET.
  const [toothPick, setToothPick] = React.useState(null);
  const pickTooth = React.useCallback(
    (fdi) => setToothPick((prev) => ({ fdi, seq: (prev?.seq || 0) + 1 })),
    [],
  );
  // A finding ticked on an image in فایل‌ها, on its way to the tooth editor
  // in the odontogram section. Lifted here for the same reason `selectedTooth`
  // is: the two panes that need it are siblings, and neither owns the other.
  const [pendingFinding, setPendingFinding] = React.useState(null);
  const [planToothRequest, setPlanToothRequest] = React.useState(null);

  // «پایان جلسه», the desktop surface's contract verbatim. The subjective form
  // is the only section holding text that is not already persisted, so the
  // screen keeps a handle on it and flushes it before closing.
  const subjectivePending = React.useRef(null);
  const [confirmFinish, setConfirmFinish] = React.useState(false);
  // «نوبت بعدی» — held HERE rather than inside the dialog so it survives the
  // re-render while `completeVisit` is in flight, and so `finishVisit` can read
  // what the dentist chose without the dialog handing it back.
  const [followUpInterval, setFollowUpInterval] = React.useState(DEFAULT_FOLLOW_UP_INTERVAL);
  const [finishError, setFinishError] = React.useState(null);

  const chart = useDentalChart({ patientId, clinicId });
  const note = useVisitNote({ patientId, appointmentId });
  const priorVisits = usePriorVisits({ patientId, appointmentId });
  const plans = useTreatmentPlans({ patientId, clinicId, appointmentId });
  const medications = useVisitMedications({ patientId, appointmentId });
  const orders = useVisitOrders({ patientId, appointmentId, clinicId });
  const diagnoses = useVisitDiagnoses({ patientId, appointmentId });
  const assistant = useClinicalAssistant({ patientId, appointmentId });
  const soap = useSoapAssistant({ patientId, appointmentId });
  const files = useVisitFiles({ patientId, appointmentId });

  const planTeeth = React.useMemo(() => planTeethIndex(plans.plans), [plans.plans]);
  const sections = React.useMemo(() => VISIT_TABS(t), [t]);
  const navItems = React.useMemo(
    () => sections.map(({ value, label, Icon }) => ({
      key: value,
      label,
      icon: <Icon size={16} aria-hidden />,
    })),
    [sections],
  );

  // The workspace's OWN mobile shape — the gradient band, the arch panel, the
  // two folded panels, the pane navigator and the pane the URL names — never a
  // generic card stack (DESIGN.md §"Loading & skeleton").
  if (loading && !visit) {
    return <VisitSkeletonMobile t={t} tab={tab} label={t('common.loading')} />;
  }

  if (error && !visit) {
    return (
      <div
        data-testid="visit-content"
        data-state="error"
        role="alert"
        style={{ padding: '0.875rem 0.75rem' }}
      >
        <ErrorState
          title={t('visit.error.title')}
          description={t('visit.error.body')}
          onRetry={reload}
          retryLabel={t('common.actions.retry')}
        />
      </div>
    );
  }

  if (!visit) {
    return (
      <div data-testid="visit-content" data-state="empty" style={{ padding: '0.875rem 0.75rem' }}>
        <Card>
          <EmptyState
            icon={<CalendarX size={40} color={C.textMuted} />}
            title={t('visit.notFound.title')}
            description={t('visit.notFound.description')}
            action={
              <Button variant="outline" className="min-h-11" onClick={goBack}>
                {t('common.actions.back')}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  const patient = visit.patient;
  /**
   * Save what is unsaved, then close the visit — the desktop's `finishVisit`,
   * same order and same irreversibility.
   *
   * `completed` makes every write on this screen refuse and this app cannot
   * reopen an appointment, so the flush goes FIRST and a failed flush aborts
   * the close. Only the subjective is flushed because only it holds unsaved
   * state: an order is written by «ثبت دستور», a plan line by its own save, a
   * tooth event as it is charted.
   */
  const finishVisit = async () => {
    setFinishError(null);

    const pending = subjectivePending.current;
    if (pending?.dirty) {
      const saveErr = await pending.submit();
      if (saveErr) {
        setFinishError({ kind: 'subjective' });
        setConfirmFinish(false);
        return;
      }
    }

    // The outstanding lines are what the dialog just showed, so the duration the
    // desk is asked to book is the longest of THOSE — not the doctor's standard
    // slot, which is a consultation and not a restoration.
    const leftUndone = outstandingForFollowUp(plans.plans, plans.records);
    const err = await complete(patientId, followUpInputFor(
      followUpInterval, Date.now(), followUpDuration(leftUndone, plans.catalog),
    ));
    if (err) {
      // The SERVICE's own sentence over the generic one: scheduling refuses a
      // transition for reasons the dentist can act on.
      setFinishError({ kind: 'complete', message: err?.message || null });
      setConfirmFinish(false);
      return;
    }
    setConfirmFinish(false);
    // The visit is closed and the screen goes read-only, so the ONLY way the
    // dentist learns whether the desk was told is this line. `unavailable` gets
    // its own sentence rather than silence — a prompt that failed to raise is a
    // patient who may never be called back.
    toast.success(finishToast(t, followUpOutcome.current));
  };

  const subtitle = [
    visit.dateTime ? formatDate(Number(visit.dateTime), 'numeric') : null,
    visit.status ? t(`today.status.${visit.status}`) : null,
  ].filter(Boolean).join(' · ');

  return (
    <div
      data-testid="visit-content"
      data-state="content"
      style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
    >
      <MobileHeader
        onBack={goBack}
        backLabel={t('common.actions.back')}
        title={patient?.name || t('visit.unknownPatient')}
        subtitle={subtitle || undefined}
        data-testid="visit-header"
        actions={[
          // Offered only when the deployment has a media server and this visit
          // is still open — the same condition the desktop header uses.
          ...(call.available && !call.live ? [{
            key: 'call',
            icon: <Video size={18} aria-hidden />,
            label: t('visit.call.start'),
            onClick: call.start,
          }] : []),
          {
            key: 'record',
            icon: <FileText size={18} aria-hidden />,
            label: t('visit.openRecord'),
            onClick: () => navigate('patient', { id: patientId }),
          },
        ]}
      />

      {/* ② An allergy must never scroll off — it is the one fact here that
          changes what may be prescribed in the دارو pane below. */}
      <div style={{ position: 'sticky', top: 0, zIndex: 3, background: C.bg }}>
        <MobileAllergyStrip allergies={patient?.allergies || []} />
      </div>

      {!editable && (
        <div style={{ padding: '0.5rem 0.75rem 0' }}>
          <Badge variant="outline">{t('visit.readOnly.badge')}</Badge>
        </div>
      )}

      {/* ③ The persistent clinical view. The ARCH stays open — that is what
          "persistent" means on this surface — and the two text-heavy sections
          below it collapse, because a phone that opens on three expanded
          sections shows none of them. */}
      <div style={{ padding: '0.75rem 0.75rem 0' }}>
        <VisitOdontogram
          pendingFinding={pendingFinding}
          onFindingConsumed={() => setPendingFinding(null)}
          patientId={patientId}
          clinicId={clinicId}
          t={t}
          formatNumber={formatNumber}
          formatDate={formatDate}
          chart={chart}
          planTeeth={planTeeth}
          records={plans.records || []}
          catalog={plans.catalog || []}
          appointmentId={appointmentId}
          selectedTooth={selectedTooth}
          onSelectTooth={setSelectedTooth}
          onToothPick={pickTooth}
          onPlanTooth={setPlanToothRequest}
          editable={editable}
        />

        <div style={{ height: '0.75rem' }} />

        {/* FOLDED BY THE PANEL ITSELF, not wrapped in a CollapsibleSection: each
            of these renders its own titled Panel, so a wrapper would draw the
            heading twice. `collapsible` is Panel's own opt-in. */}
        <VisitSubjective
          collapsible
          defaultOpen={false}
          pendingRef={subjectivePending}
          t={t}
          formatNumber={formatNumber}
          formatDate={formatDate}
          note={note}
          editable={editable}
          selectedTooth={selectedTooth}
          toothPick={toothPick}
          priorVisits={priorVisits}
          patient={patient}
          onOpenPriorVisit={(id) => navigate('visit', { appointmentId: id })}
        />

        <div style={{ height: '0.5rem' }} />

        <VisitTreatmentPlan
          collapsible
          defaultOpen={false}
          t={t}
          formatNumber={formatNumber}
          plans={plans}
          catalog={plans.catalog}
          clinicId={clinicId}
          appointmentId={appointmentId}
          editable={editable}
          selectedTooth={selectedTooth}
          onSelectTooth={setSelectedTooth}
          addRequestTooth={planToothRequest}
          onAddRequestHandled={() => setPlanToothRequest(null)}
          onChartChanged={chart.reload}
        />
      </div>

      {/* ④ The pane navigator, pinned: a pane is always one tap away however
          far down the arch the dentist has scrolled. */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 2,
        background: C.bg, marginTop: '0.75rem',
      }}>
        <SectionNav
          items={navItems}
          active={tab}
          onSelect={setTab}
          ariaLabel={t('visit.tabs.sectionsLabel')}
          data-testid="visit-nav"
        />
      </div>

      {/* ⑤ The kit's SectionNav declares role="tablist"/role="tab"; this is the
          other half. `aria-label` rather than `aria-labelledby` — the kit
          generates no ids. */}
      <div
        role="tabpanel"
        aria-label={sections.find((s) => s.value === tab)?.label}
        style={{ padding: '0.875rem 0.75rem 1.5rem' }}
      >
        <Card style={{ padding: '0.875rem', background: CT.surface }}>
          {tab === 'orders' ? (
            <OrdersPane orders={orders} editable={editable} />
          ) : tab === 'diagnosis' ? (
            <DiagnosisPane
              diagnoses={diagnoses}
              note={note}
              files={files.files}
              soap={soap}
              chart={chart}
              visit={visit}
              patient={patient}
              editable={editable}
              selectedTooth={selectedTooth}
              onSelectTooth={setSelectedTooth}
            />
          ) : tab === 'questions' ? (
            <QuestionsPane
              assistant={assistant}
              diagnoses={diagnoses}
              note={note}
              editable={editable}
            />
          ) : tab === 'files' ? (
            <FilesPane
              files={files}
              visit={visit}
              editable={editable}
              selectedTooth={selectedTooth}
              compact
              onConfirmFinding={setPendingFinding}
            />
          ) : (
            <MedicationsPane
                visit={visit}
                patient={patient}
                editable={editable}
                medications={medications}
              />
          )}
        </Card>
      </div>

      {/* ⑥ «پایان جلسه» — DOCKED, inside the visit page.
          The desktop keeps it in the page header, which a phone does not have:
          §4C gives the mobile header the back arrow and two icon actions, and
          the one irreversible write of this screen is not an icon. So it docks
          at the bottom of the visit's own scroll, the shape نسخه already uses
          for its commit row (§6: "submit docks bottom") — always reachable
          however deep into a pane the dentist has scrolled, and never mistaken
          for navigation.

          Rendered only while the visit is OPEN: once closed the read-only badge
          above says so, and a disabled «پایان جلسه» would just repeat it. */}
      {editable && (
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 4,
            background: C.card,
            borderTop: `1px solid ${C.border}`,
            padding: '0.75rem 0.75rem calc(0.75rem + env(safe-area-inset-bottom, 0px))',
          }}
        >
          {/* Says WHICH half failed, because the two need different actions: a
              rejected save leaves the visit open with the text still on screen
              to retry, while a rejected close means the record is saved and
              only the appointment is still open. In flow directly above the
              button, so the answer is where the press was. */}
          {finishError && (
            <div role="alert" style={{ marginBottom: '0.625rem' }}>
              <Alert
                variant="error"
                title={t(`visit.finish.error.${finishError.kind}`)}
              >
                {/* The kit's Alert takes the body as CHILDREN. As a `description` prop it
                    was spread onto the <div> as an unknown attribute, so the service's
                    own sentence — the whole reason this branch prefers it — reached the
                    DOM and rendered nothing. */}
                {finishError.message || null}
              </Alert>
            </div>
          )}
          <Button
            onClick={() => setConfirmFinish(true)}
            disabled={completing}
            className="w-full min-h-11"
            data-testid="visit-finish"
          >
            <CheckCircle2 size={16} aria-hidden />
            {completing ? t('visit.finish.working') : t('visit.finish.action')}
          </Button>
        </div>
      )}

      {/* The close is irreversible — this app cannot reopen an appointment — so
          it is confirmed on the phone exactly as it is on the workstation. */}
      {/* «پایان جلسه», and — only when this visit leaves work planned and not
          performed — «نوبت بعدی». The one moment the dentist is certainly
          thinking about whether the patient must come back is the moment they
          close the visit, so that is where they are asked. */}
      <FinishVisitDialog
        open={confirmFinish}
        onOpenChange={setConfirmFinish}
        onConfirm={finishVisit}
        loading={completing}
        t={t}
        formatNumber={formatNumber}
        outstanding={outstandingForFollowUp(plans.plans, plans.records)}
        interval={followUpInterval}
        onIntervalChange={setFollowUpInterval}
        testId="visit-finish-confirm"
        confirmTestId="visit-finish-confirm-yes"
        cancelTestId="visit-finish-confirm-no"
      />

      {/* Same panel as the desktop surface. On a phone it sits above the thumb
          zone rather than beside the workspace, which is the only thing about
          it that differs. */}
      <VisitCall call={call} patientName={patient?.name} />
    </div>
  );
};

export default VisitMobile;
